import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["altText", "backgroundColor", "backgroundColorToken", "backgroundColorTokenArgs", "disabled", "fallbackCharacter", "hasBorder", "icon", "imgResizeMode", "large", "loading", "medium", "small", "textColor", "textColorToken", "textColorTokenArgs", "white", "source", "square", "onError", "UNSAFE_containerProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import Text from '@bubble-ui/primitive-text';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import { useDesignContext } from '@klarna/designtokens';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import usePropDeprecationWarning from '@bubble-ui/hook-use-prop-deprecation-warning';
import Loader, { getPostFix, loaderSizes } from '@bubble-ui/molecule-loader';
import View from '@bubble-ui/primitive-view';
import { isNative } from '@bubble-ui/util-platform';
import defaultDesignTokens from './defaultDesignTokens';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import AvatarImage from './avatarImage';
var SIZE_SMALL = 30;
var SIZE_MEDIUM = 40;
var SIZE_DEFAULT = 50;
var SIZE_LARGE = 70;

var Avatar = function Avatar(_ref) {
  var _ref$altText = _ref.altText,
      altText = _ref$altText === void 0 ? 'Avatar' : _ref$altText,
      backgroundColor = _ref.backgroundColor,
      backgroundColorToken = _ref.backgroundColorToken,
      backgroundColorTokenArgs = _ref.backgroundColorTokenArgs,
      disabled = _ref.disabled,
      fallbackCharacter = _ref.fallbackCharacter,
      _ref$hasBorder = _ref.hasBorder,
      hasBorder = _ref$hasBorder === void 0 ? true : _ref$hasBorder,
      Icon = _ref.icon,
      _ref$imgResizeMode = _ref.imgResizeMode,
      imgResizeMode = _ref$imgResizeMode === void 0 ? 'contain' : _ref$imgResizeMode,
      large = _ref.large,
      loading = _ref.loading,
      medium = _ref.medium,
      small = _ref.small,
      textColor = _ref.textColor,
      textColorToken = _ref.textColorToken,
      textColorTokenArgs = _ref.textColorTokenArgs,
      white = _ref.white,
      source = _ref.source,
      _ref$square = _ref.square,
      square = _ref$square === void 0 ? false : _ref$square,
      onError = _ref.onError,
      UNSAFE_containerProps = _ref.UNSAFE_containerProps,
      props = _objectWithoutProperties(_ref, _excluded);

  usePropDeprecationWarning({
    componentName: 'Avatar',
    propName: 'backgroundColor',
    useInstead: 'backgroundColorToken',
    shouldWarn: backgroundColor !== undefined,
    removalDate: 'v5.0.0'
  });
  usePropDeprecationWarning({
    componentName: 'Avatar',
    propName: 'textColor',
    useInstead: 'textColorToken',
    shouldWarn: textColor !== undefined,
    removalDate: 'v5.0.0'
  });
  var avatarImageSize = small ? SIZE_SMALL : medium ? SIZE_MEDIUM : large ? SIZE_LARGE : SIZE_DEFAULT;
  var avatarSize = small ? 'small' : medium ? 'medium' : large ? 'large' : 'regular';
  var sizeName = getPostFix({
    avatarSize: avatarSize
  });
  var loaderSize = loaderSizes[sizeName];
  var loaderMargin = (avatarImageSize - loaderSize) / 2;
  var shouldShowBorder = hasBorder && (source != null || white && Icon != null);
  var iconProps = large ? {
    colorToken: 'colors/text/default'
  } : {};
  var designVersion = useDesignVersion();
  var designContext = useDesignContext(defaultDesignTokens);

  var _useStyleSheet = useStyleSheet(Avatar, getDefaultStyleSheet, _objectSpread({
    designVersion: designVersion,
    designContext: designContext,
    altText: altText,
    backgroundColor: backgroundColor,
    backgroundColorToken: backgroundColorToken,
    backgroundColorTokenArgs: backgroundColorTokenArgs,
    disabled: disabled,
    fallbackCharacter: fallbackCharacter,
    hasBorder: hasBorder,
    icon: Icon,
    imgResizeMode: imgResizeMode,
    large: large,
    medium: medium,
    small: small,
    textColor: textColor,
    textColorToken: textColorToken,
    textColorTokenArgs: textColorTokenArgs,
    white: white,
    source: source,
    square: square,
    onError: onError,
    avatarImageSize: avatarImageSize
  }, props)),
      textStyle = _useStyleSheet.textStyle,
      viewStyle = _useStyleSheet.viewStyle,
      borderStyle = _useStyleSheet.borderStyle,
      imgStyle = _useStyleSheet.imgStyle,
      iconViewStyle = _useStyleSheet.iconViewStyle,
      hasSourceStyle = _useStyleSheet.hasSourceStyle;

  return /*#__PURE__*/React.createElement(View, null, /*#__PURE__*/React.createElement(View, _extends({}, UNSAFE_containerProps, {
    style: viewStyle
  }, props), fallbackCharacter && !source && !Icon && /*#__PURE__*/React.createElement(Text, {
    style: textStyle
  }, fallbackCharacter.toUpperCase()), Icon && !source && /*#__PURE__*/React.createElement(View, {
    style: iconViewStyle
  }, /*#__PURE__*/React.createElement(Icon, iconProps)), Boolean(source) && /*#__PURE__*/React.createElement(AvatarImage, {
    avatarImageSize: avatarImageSize,
    altText: altText,
    source: source,
    imgResizeMode: imgResizeMode,
    imgStyle: imgStyle,
    hasSourceStyle: hasSourceStyle,
    onError: onError
  }), shouldShowBorder && /*#__PURE__*/React.createElement(View, {
    style: _objectSpread(_objectSpread({}, !isNative ? hasSourceStyle : {}), borderStyle)
  })), loading && /*#__PURE__*/React.createElement(View, {
    style: {
      marginBottom: loaderMargin,
      marginLeft: loaderMargin,
      marginRight: loaderMargin,
      marginTop: loaderMargin - avatarImageSize
    }
  }, /*#__PURE__*/React.createElement(Loader, {
    UNSAFE_avatarSize: avatarSize
  })));
};

Avatar.displayName = 'Avatar';
export default withProvisionalOverrideFromContext(Avatar);
//# sourceMappingURL=index.js.map